<template>
    <div>
        <div v-if="! subscriptionMissing">
            <modal-template ref="modal" modal-size="2xl">
                <template v-slot:title>
                    {{ lang.modals.offer_stats?.headline }}
                </template>

                <template v-slot:default>
                    <p>{{lang.modals.offer_stats?.p1}}</p>

                    <!-- TABS -->
                    <div class="border-b border-gray-200">
                        <nav class="-mb-px flex space-x-8 tabs-desktop" aria-label="Tabs">
                            <a @click.prevent="switchTab('longTerm')" href="#" :class="{inactive: activeTab !== 'longTerm', active: activeTab === 'longTerm'}">{{ lang.labels.stats_long_term }}</a>
                            <a @click.prevent="switchTab('shortTerm')" href="#" :class="{inactive: activeTab !== 'shortTerm', active: activeTab === 'shortTerm'}">{{ lang.labels.stats_short_term }}</a>
                        </nav>
                    </div>

                    <!-- STATS-->
                    <div class="tab-content mt-4">
                        <div id="longTerm" v-show="activeTab == 'longTerm'">
                            <canvas id="phChartLongTerm" class="phChartLongTerm" ref="phChartLongTerm" width="880" height="400"></canvas>
                        </div>
                        <div id="shortTerm" v-show="activeTab == 'shortTerm'">
                            <canvas id="phChartShortTerm" class="phChartShortTerm" ref="phChartShortTerm" width="880" height="400"></canvas>
                        </div>
                        <small>{{lang.modals.offer_stats?.p1}}</small>
                    </div>

                </template>

                <template v-slot:footer="footerSlotProps">
                    <plain-button @click="hideModal()">{{ lang.labels.close }}</plain-button>
                </template>

            </modal-template>
        </div>

        <div v-if="subscriptionMissing">
            <modal-template ref="modal" modal-size="xl">
                <template v-slot:title>
                    {{ lang.modals.statsSubscriptionRequired?.headline }}
                </template>

                <template v-slot:default>
                    <h2 class="text-lg">{{ lang.modals.statsSubscriptionRequired?.sub_headline }}</h2>
                    <p class="text-center" v-html="lang.modals.statsSubscriptionRequired?.p1"></p>
                </template>

                <template v-slot:footer="footerSlotProps">
                    <div class="flex gap-4">
                        <plain-button color="default" @click="hideModal()">{{ lang.labels.close }}</plain-button>
                        <plain-button :href="urlSubscriptionShow" color="primary">{{lang.nav.section_premium_member}}</plain-button>
                    </div>
                </template>

            </modal-template>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue';
import ModalTemplate from "./ModalTemplate.vue";

const props = defineProps({
    lang: Object,
    modalData: Object,
});

const { proxy } = getCurrentInstance();

const modal = ref(null);
const offerViewStats = ref('');
const subscriptionMissing = ref(false);
const colorSets = ref([]);
const activeTab = ref('longTerm');
const charts = reactive({
    longTerm: null,
    shortTerm: null,
});

const urlSubscriptionShow = computed(() => {
    return '/user/subscription';
});

const showModal = () => {
    modal.value.showModal();
    init();
};

const hideModal = () => {
    modal.value.hideModal();
};

const init = () => {
    loadStats();
};

const loadStats = () => {
    axios.get(`/web-api/user/offer/offer-stats-modal`, {
            params: {
                offers: props.modalData.offers
            }
        })
        .then(response => {
            offerViewStats.value = response.data.offerViewStats;
            initChart('longTerm');
        })
        .catch(error => {
            let errorMsg = error.response.data.error;

            if(errorMsg === 'subscription missing') {
                showSubscriptionMissing();
            }
        });
};

const switchTab = (chartTermType) => {
    activeTab.value = chartTermType;

    if(charts[chartTermType] !== null) {
        return;
    }

    setTimeout(() => {
        initChart(chartTermType);
    }, 250);
};

const initChart = (chartTermType) => {
    let rawDataset = offerViewStats.value[chartTermType];

    let chartData = {
        labels: rawDataset.labels,
        datasets: generateDataset(rawDataset),
    };

    let chartOptions = {
        legend: {
            position: 'bottom',
            align: 'start',
        },
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true
                }
            }]
        }
    };
    let querySelector = (chartTermType === 'longTerm') ? '.phChartLongTerm' : '.phChartShortTerm';
    let ctx = proxy.$el.querySelector(querySelector).getContext('2d');
    charts[chartTermType] = new Chart(ctx, {
        type: 'line',
        data: chartData,
        options: chartOptions
    });
};

const generateDataset = (rawDataset) => {
    let datasets = [];

    _.forEach(props.modalData.offers, (model, index) => {
        let offerId = model;

        let dataset = {
            label: rawDataset[offerId].label,
            data: _.values(rawDataset[offerId].stats),
            backgroundColor: colorSets.value[index].backgroundColor,
            borderColor: colorSets.value[index].borderColor,
            borderWidth: 1,
        };

        datasets.push(dataset);
    });

    return datasets;
};

const showSubscriptionMissing = () => {
    subscriptionMissing.value = true;
};

const setColorSet = () => {
    // Color set 1
    colorSets.value.push({
        backgroundColor: "rgba(138,168,118,0.2)",
        borderColor: "rgba(138,168,118,1)",
        pointColor: "rgba(138,168,118,1)",
        pointStrokeColor: "#fff",
        pointHighlightFill: "#fff",
        pointHighlightStroke: "rgba(138,168,118,1)",
    });

    // Color set 2
    colorSets.value.push({
        backgroundColor: "rgba(113,141,171,0.2)",
        borderColor: "rgba(113,141,171,1)",
        pointColor: "rgba(113,141,171,1)",
        pointStrokeColor: "#fff",
        pointHighlightFill: "#fff",
        pointHighlightStroke: "rgba(113,141,171,1)",
    });

    // Color set 3
    colorSets.value.push({
        backgroundColor: "rgba(0,179,176,0.2)",
        borderColor: "rgba(0,179,176,1)",
        pointColor: "rgba(0,179,176,1)",
        pointStrokeColor: "#fff",
        pointHighlightFill: "#fff",
        pointHighlightStroke: "rgba(0,179,176,1)",
    });

    // Color set 4
    colorSets.value.push({
        backgroundColor: "rgba(240,101,75,0.2)",
        borderColor: "rgba(240,101,75,1)",
        pointColor: "rgba(240,101,75,1)",
        pointStrokeColor: "#fff",
        pointHighlightFill: "#fff",
        pointHighlightStroke: "rgba(240,101,75,1)",
    });

    // Color set 5
    colorSets.value.push({
        backgroundColor: "rgba(194,111,38,0.2)",
        borderColor: "rgba(194,111,38,1)",
        pointColor: "rgba(194,111,38,1)",
        pointStrokeColor: "#fff",
        pointHighlightFill: "#fff",
        pointHighlightStroke: "rgba(194,111,38,1)",
    });

    // Color set 6
    colorSets.value.push({
        backgroundColor: "rgba(156,78,139,0.2)",
        borderColor: "rgba(156,78,139,1)",
        pointColor: "rgba(156,78,139,1)",
        pointStrokeColor: "#fff",
        pointHighlightFill: "#fff",
        pointHighlightStroke: "rgba(156,78,139,1)",
    });
};

onMounted(() => {
    setColorSet();
});

defineExpose({
    showModal,
    hideModal,
});
</script>
